import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { API_URL, BACKEND_URL } from '../config';
import { ErrorState } from '../components/ErrorState';
import { classifyError } from '../lib/apiError';
import {
  TrendingUp, TrendingDown, PieChart, BarChart3, Target, Lightbulb,
  RefreshCw, DollarSign, Calendar, ChevronRight, AlertCircle
} from "lucide-react";


export default function InsightsPage() {
  const [dashboard, setDashboard] = useState(null);
  const [trends, setTrends] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [tips, setTips] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [budgetCategory, setBudgetCategory] = useState("total");
  const [budgetLimit, setBudgetLimit] = useState("");

  const token = localStorage.getItem("auth_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashRes, trendRes, predRes, tipsRes, budgetRes] = await Promise.all([
        axios.get(`${API_URL}/insights/dashboard`, { headers }),
        axios.get(`${API_URL}/insights/spending-trend?months=6`, { headers }),
        axios.get(`${API_URL}/insights/predictions`, { headers }),
        axios.get(`${API_URL}/insights/tips`, { headers }),
        axios.get(`${API_URL}/insights/budgets`, { headers })
      ]);
      setDashboard(dashRes.data);
      setTrends(trendRes.data);
      setPredictions(predRes.data);
      setTips(tipsRes.data);
      setBudgets(budgetRes.data);
    } catch (err) {
      console.error("Failed to load insights", err);
      setError(classifyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSetBudget = async (e) => {
    e.preventDefault();
    if (!budgetLimit || parseFloat(budgetLimit) < 1000) {
      toast.error("Minimum budget is MWK 1,000");
      return;
    }
    try {
      await axios.post(`${API_URL}/insights/budgets`, {
        category: budgetCategory,
        monthly_limit: parseFloat(budgetLimit),
        alert_threshold: 80
      }, { headers });
      toast.success("Budget set!");
      setShowBudgetModal(false);
      setBudgetLimit("");
      fetchData();
    } catch (error) {
      toast.error("Failed to set budget");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchData} />;
  }

  const summary = dashboard?.summary || {};
  const byCategory = dashboard?.by_category || {};

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6" data-testid="insights-page">
      <div>
        <h1 className="text-2xl font-bold">Where the household money went</h1>
        <p className="text-sm text-muted-foreground">A friendly look at electricity, water, and TV this month.</p>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-5 h-5 text-emerald-500" />
            {summary.change_direction === "up" ? (
              <TrendingUp className="w-4 h-4 text-red-500" />
            ) : summary.change_direction === "down" ? (
              <TrendingDown className="w-4 h-4 text-green-500" />
            ) : null}
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white" data-testid="current-month-total">
            MWK {summary.current_month_total?.toLocaleString() || "0"}
          </p>
          <p className="text-sm text-gray-500">This Month</p>
          {summary.change_percent !== 0 && (
            <p className={`text-xs mt-1 ${summary.change_direction === "up" ? "text-red-500" : "text-green-500"}`}>
              {summary.change_direction === "up" ? "+" : ""}{summary.change_percent?.toFixed(1)}% vs last month
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-2">
            <BarChart3 className="w-5 h-5 text-blue-500" />
            <span className="text-xs text-gray-400">{summary.transaction_count || 0} txns</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            MWK {summary.current_month_fees?.toLocaleString() || "0"}
          </p>
          <p className="text-sm text-gray-500">Fees Paid (4%)</p>
        </motion.div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <PieChart className="w-5 h-5 text-primary" />
            Spending by Category
          </h3>
        </div>
        <div className="p-4 space-y-4">
          {Object.entries(byCategory).map(([category, data]) => {
            const colors = {
              electricity: "bg-yellow-500",
              water: "bg-blue-500",
              tv: "bg-purple-500",
              other: "bg-gray-500"
            };
            const total = Object.values(byCategory).reduce((sum, c) => sum + (c.total || 0), 0);
            const percent = total > 0 ? ((data.total || 0) / total) * 100 : 0;
            
            return (
              <div key={category}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600 dark:text-gray-400 capitalize">{category}</span>
                  <span className="text-gray-900 dark:text-white font-medium">
                    MWK {(data.total || 0).toLocaleString()}
                  </span>
                </div>
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${colors[category] || "bg-gray-500"} rounded-full transition-all`}
                    style={{ width: `${percent}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{data.count || 0} transactions</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Spending Trend */}
      {trends && trends.trends && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              6-Month Trend
            </h3>
          </div>
          <div className="p-4">
            <div className="flex items-end justify-between h-32 gap-2">
              {trends.trends.map((month, i) => {
                const maxTotal = Math.max(...trends.trends.map(t => t.total || 0));
                const height = maxTotal > 0 ? ((month.total || 0) / maxTotal) * 100 : 0;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-emerald-500 rounded-t-lg transition-all"
                      style={{ height: `${height}%`, minHeight: month.total > 0 ? "4px" : "0" }}
                    />
                    <p className="text-xs text-gray-500 mt-2">{month.month}</p>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 text-sm text-center">
              <span className="text-gray-500">Average: </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                MWK {trends.average_monthly?.toLocaleString() || "0"}/month
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Predictions */}
      {predictions && (
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-4 text-white">
          <h3 className="font-semibold flex items-center gap-2 mb-3">
            <Target className="w-5 h-5" />
            Predictions
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-emerald-100">Projected This Month</p>
              <p className="text-xl font-bold">
                MWK {predictions.current_month?.projected_total?.toLocaleString() || "0"}
              </p>
            </div>
            <div>
              <p className="text-sm text-emerald-100">Next Month Estimate</p>
              <p className="text-xl font-bold">
                MWK {predictions.next_month_prediction?.toLocaleString() || "0"}
              </p>
            </div>
          </div>
          <p className="text-xs text-emerald-200 mt-3">
            {predictions.current_month?.days_remaining || 0} days remaining this month
          </p>
        </div>
      )}

      {/* Budgets */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-orange-500" />
            Budgets
          </h3>
          <button
            onClick={() => setShowBudgetModal(true)}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            + Set Budget
          </button>
        </div>
        <div className="p-4">
          {budgets.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No budgets set. Set one to track spending!</p>
          ) : (
            <div className="space-y-4">
              {dashboard?.budgets?.map((budget) => (
                <div key={budget.category}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-gray-400 capitalize">{budget.category}</span>
                    <span className={budget.alert ? "text-red-500 font-medium" : "text-gray-900 dark:text-white"}>
                      MWK {budget.spent?.toLocaleString()} / {budget.limit?.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        budget.percent_used > 90 ? "bg-red-500" : 
                        budget.percent_used > 70 ? "bg-orange-500" : "bg-green-500"
                      }`}
                      style={{ width: `${Math.min(budget.percent_used, 100)}%` }}
                    />
                  </div>
                  {budget.alert && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Approaching limit!
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Saving Tips */}
      {tips.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-500" />
              Saving Tips
            </h3>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {tips.map((tip, i) => (
              <div key={i} className="flex items-start gap-3 p-4">
                <span className="text-2xl">{tip.icon}</span>
                <div className="flex-1">
                  <p className="text-gray-900 dark:text-white text-sm">{tip.tip}</p>
                  <p className="text-xs text-green-600 mt-1">{tip.potential_savings}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Budget Modal */}
      {showBudgetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md"
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Set Budget</h3>
            <form onSubmit={handleSetBudget} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                <select
                  value={budgetCategory}
                  onChange={(e) => setBudgetCategory(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="total">Total Spending</option>
                  <option value="electricity">Electricity</option>
                  <option value="water">Water</option>
                  <option value="tv">TV Subscriptions</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Monthly Limit (MWK)</label>
                <input
                  type="number"
                  value={budgetLimit}
                  onChange={(e) => setBudgetLimit(e.target.value)}
                  placeholder="e.g., 50000"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowBudgetModal(false)}
                  className="flex-1 py-3 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 rounded-xl bg-emerald-600 text-white"
                >
                  Set Budget
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}